import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Product } from "../../../domain/entities/product/Product";
import { Experiments } from "../../../domain/repositories/ABTestClient";
import { financial } from "../shared/util/financial/financial";
import { experimentNameShortener } from "./experimentNameShortener";
import { Constants, ItemDetailDisplayedTrackingEvent, PROJECT, TrackingEventName } from "./tracking";

interface TrackItemDetailDisplayedFunctionArgs {
  readonly item: Product;
  readonly lookId: string;
  readonly lastUnits: boolean;
  readonly experiments: Experiments | undefined;
}

interface TrackItemDetailDisplayedFunction {
  (args: TrackItemDetailDisplayedFunctionArgs): void;
}

interface UseTrackItemDetailDisplayedFunctionArgs {
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackItemDetailDisplayedFunction {
  (agrs: UseTrackItemDetailDisplayedFunctionArgs): TrackItemDetailDisplayedFunction;
}

const useTrackItemDetailDisplayed: UseTrackItemDetailDisplayedFunction = ({ country, segment, tradename, userId }) => {
  const emitUserEvent = useEmitUserEvent<ItemDetailDisplayedTrackingEvent>();

  const trackItemDetailDisplayed: TrackItemDetailDisplayedFunction = useCallback(
    ({ item, experiments, lastUnits, lookId }) => {
      const isSalesProduct = item.price?.discountedPrice ? item.price.discountedPrice.percentage > 0 : undefined;

      emitUserEvent({
        event: TrackingEventName.ITEM_DETAIL_DISPLAYED,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        variant_id: item.variantId,
        last_units: lastUnits,
        sales: isSalesProduct,
        pct_discount: isSalesProduct ? item.price?.discountedPrice?.percentage : undefined,
        experiments: experiments ? JSON.stringify(experimentNameShortener(experiments)) : undefined,
        ecommerce: {
          detail: {
            actionField: {
              list: Constants.COMPLETE_YOUR_LOOK_LIST,
            },
          },
          products: [
            {
              name: item.title,
              id: item.variantId,
              price: financial(item.price.amount),
              brand: item.brand,
              category: Constants.NOT_SET,
              variant: item.size,
              list: Constants.COMPLETE_YOUR_LOOK_LIST,
              index: Constants.INDEX,
              look_id: lookId,
              quantity: Constants.QUANTITY,
            },
          ],
        },
      });
    },
    [country, emitUserEvent, segment, tradename, userId],
  );

  return trackItemDetailDisplayed;
};

export { useTrackItemDetailDisplayed };
